import { useState } from 'react';
import { SectionTitle, SubTitle, CodeBlock, InfoBox, Badge } from './shared';

export default function SkillsInstallContent() {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyCode = (id: string, code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const InlineCode = ({ children }: { children: React.ReactNode }) => (
    <code className="text-cyan-400 bg-slate-800/60 px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
  );

  const CopyableCode = ({ id, code }: { id: string; code: string }) => (
    <div className="relative group my-3">
      <div className="flex items-center justify-between bg-slate-800 rounded-t-lg px-4 py-2 border border-slate-700 border-b-0">
        <span className="text-xs text-slate-400 font-mono">bash</span>
        <button
          onClick={() => copyCode(id, code)}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className={copiedId === id ? 'ri-check-line text-emerald-400' : 'ri-file-copy-line'}></i>
          {copiedId === id ? '已复制' : '复制'}
        </button>
      </div>
      <pre className="bg-slate-950 border border-slate-700 rounded-b-lg p-4 overflow-x-auto">
        <code className="text-sm text-slate-300 font-mono leading-relaxed">{code}</code>
      </pre>
    </div>
  );

  const StepCard = ({
    step,
    title,
    children,
  }: {
    step: number;
    title: string;
    children: React.ReactNode;
  }) => (
    <div className="flex gap-4 mb-6">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-cyan-400 text-sm font-bold">
        {step}
      </div>
      <div className="flex-1 pt-0.5">
        <div className="text-sm font-semibold text-white mb-2">{title}</div>
        <div className="text-sm text-slate-400 leading-relaxed">{children}</div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Badge color="cyan">安装指南</Badge>
          <Badge color="teal">ClawHub</Badge>
        </div>
        <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
          安装登录 ClawHub 并接入 Skills
        </h1>
        <p className="text-slate-400 text-base leading-relaxed">
          本文详细介绍 ClawHub 的安装、登录流程以及为 OpenClaw 接入 skills 的具体方法，包括命令行安装、Bot 辅助安装和手动安装三种方式。
        </p>
      </div>

      {/* 前言 */}
      <SectionTitle id="intro">1. 前言</SectionTitle>
      <p className="text-slate-400 text-sm leading-relaxed mb-4">
        本文基于 OpenClaw 2026.3.1 官方一键脚本直装版本。
      </p>

      {/* 场景 */}
      <SectionTitle id="scene">2. 场景说明</SectionTitle>
      <p className="text-slate-400 text-sm leading-relaxed mb-4">
        在装完浏览器后，想起了在搭建 OpenClaw 时跳过的一个步骤：安装 skills。当让 Bot 帮忙安装时，会遇到 ClawHub 限频的问题。
      </p>
      <InfoBox type="info">
        ClawHub 的限频策略比较严格。遇到限频时，可以通过下面介绍的方式来安装 skills。
      </InfoBox>

      {/* 安装并登录 ClawHub */}
      <SectionTitle id="install-clawhub">3. 安装并登录 ClawHub</SectionTitle>
      <p className="text-slate-400 text-sm leading-relaxed mb-5">
        此步骤为可选项，主要用于获取 API Token 以解决限频问题。
      </p>

      <SubTitle>3.1 安装 ClawHub CLI</SubTitle>
      <p className="text-slate-400 text-sm leading-relaxed mb-3">
        首先，在服务器上运行以下命令安装 ClawHub：
      </p>
      <CopyableCode id="install-cmd" code="npm i -g clawhub" />

      <SubTitle>3.2 登录 ClawHub 获取 Token</SubTitle>
      <div className="space-y-1 mb-4">
        <StepCard step={1} title="使用 GitHub 登录 ClawHub 官网">
          打开{' '}
          <a href="https://clawhub.ai/" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">
            https://clawhub.ai/
          </a>
          ，点击使用 GitHub 登录（需要先登录 GitHub）。
        </StepCard>

        <StepCard step={2} title="跳转到授权页面，点击授权">
          GitHub 会跳转到授权页面，点击授权按钮即可。
        </StepCard>

        <StepCard step={3} title="进入设置页面">
          点击自己的用户名，然后点击「设置」。
        </StepCard>

        <StepCard step={4} title="创建 API Token">
          下拉到 <InlineCode>API tokens</InlineCode> 区域，点击 <InlineCode>Create token</InlineCode>。
        </StepCard>

        <StepCard step={5} title="复制生成的 Token">
          复制生成出的 Token，下面登录命令中会用到。
        </StepCard>

        <StepCard step={6} title="在服务器上执行登录命令">
          将下方命令中的 <InlineCode>你的Token</InlineCode> 替换为刚才复制的 Token，注意 <InlineCode>--token</InlineCode> 后面有空格：
        </StepCard>
      </div>

      <CopyableCode id="login-cmd" code="clawhub login --token 你的Token" />

      {/* 接入 Skills */}
      <SectionTitle id="install-skills">4. 接入 Skills</SectionTitle>
      <InfoBox type="warning">
        下方 4.1 和 4.2 方式会受 ClawHub 限频影响，如果遇到限频可以直接看 4.3 手动接入。
      </InfoBox>

      {/* 4.1 Bot 帮忙接入 */}
      <SubTitle id="bot-install">4.1 让 Bot 帮忙接入</SubTitle>
      <p className="text-slate-400 text-sm leading-relaxed mb-4">
        可以直接和 Bot 对话，让它找找热门的 skills 并安装。
      </p>
      <InfoBox type="tip">
        有时候限频也能用 Bot 这种方式解决，Bot 会自动绕过限频限制，非常厉害！
      </InfoBox>

      {/* 4.2 命令行接入 */}
      <SubTitle id="cli-install">4.2 命令行接入</SubTitle>
      <p className="text-slate-400 text-sm leading-relaxed mb-4">
        访问{' '}
        <a
          href="https://clawhub.ai/skills?sort=downloads"
          target="_blank"
          rel="noopener noreferrer"
          className="text-cyan-400 hover:underline"
        >
          https://clawhub.ai/skills?sort=downloads
        </a>
        ，找到想安装的 skill，复制技能页面中显示的 slug 名称。
      </p>

      <p className="text-slate-400 text-sm leading-relaxed mb-3">
        在服务器运行以下命令，把 <InlineCode>&lt;slug&gt;</InlineCode> 替换为上面复制的名字（不要带 <InlineCode>/</InlineCode>）：
      </p>
      <CopyableCode id="install-skill-cmd" code="clawhub install <slug>" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div className="bg-slate-800/40 border border-rose-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-error-warning-line text-rose-400 text-sm"></i>
            </div>
            <span className="text-xs font-semibold text-rose-400">被限频时</span>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">终端会提示 rate limit 相关错误，此时需要登录 Token 或改用手动安装方式。</p>
        </div>
        <div className="bg-slate-800/40 border border-emerald-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-emerald-400 text-sm"></i>
            </div>
            <span className="text-xs font-semibold text-emerald-400">安装成功时</span>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">终端会显示 skill 安装完成的提示，重启 OpenClaw 后即可在 Bot 中使用该技能。</p>
        </div>
      </div>

      {/* 4.3 手动接入 */}
      <SubTitle id="manual-install">4.3 手动接入</SubTitle>
      <p className="text-slate-400 text-sm leading-relaxed mb-4">
        如果命令行方式遇到限频，可以手动下载安装。点进想要安装的 skill 页面，手动下载压缩包，然后将压缩包上传到服务器的以下目录，解压即可：
      </p>

      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
            <i className="ri-folder-line text-cyan-400 text-lg"></i>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">默认工作区路径</div>
            <code className="text-sm font-mono text-cyan-300">/root/.openclaw/workspace/skills</code>
          </div>
        </div>
      </div>

      <InfoBox type="tip">
        如果你使用的是其它工作区，则将路径替换为对应的 <code className="text-cyan-400 text-xs font-mono">工作区目录/skills</code>，解压后重启 OpenClaw 即可生效。
      </InfoBox>

      {/* 总结 */}
      <SectionTitle id="summary">5. 总结</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {[
          {
            icon: 'ri-robot-line',
            title: '方式一：Bot 安装',
            desc: '直接和 Bot 对话让它安装，有时可绕过限频，最简单。',
            color: 'cyan',
          },
          {
            icon: 'ri-terminal-line',
            title: '方式二：命令行',
            desc: '使用 clawhub install 命令安装，需要登录 Token，受限频影响。',
            color: 'teal',
          },
          {
            icon: 'ri-download-2-line',
            title: '方式三：手动安装',
            desc: '下载压缩包上传到 skills 目录解压，不受限频影响，最稳定。',
            color: 'emerald',
          },
        ].map((item, i) => (
          <div
            key={i}
            className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 flex flex-col gap-2"
          >
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-${item.color}-500/10 border border-${item.color}-500/20`}>
              <i className={`${item.icon} text-${item.color}-400 text-base`}></i>
            </div>
            <div className="text-sm font-semibold text-white">{item.title}</div>
            <div className="text-xs text-slate-400 leading-relaxed">{item.desc}</div>
          </div>
        ))}
      </div>

      <InfoBox type="info">
        原文来源：
        <a
          href="https://cloud.tencent.com/developer/article/2634137"
          target="_blank"
          rel="noopener noreferrer"
          className="text-cyan-400 hover:underline ml-1"
        >
          安装登录ClawHub并给OpenClaw接入skills — 腾讯云开发者社区
        </a>
        ，作者：猫猫摸大鱼
      </InfoBox>
    </div>
  );
}
